import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ToDoMgmtSubType } from '../../../../enum';
import Buttons from '../../../../components/common/Buttons';
import TextWinds from '../../../../components/common/TextWinds';
import HFbutton from '../../../../components/common/input/HFbutton';
import { openNewWindow, getSubTypeName } from '../../../../api/utils/util-func';
import RegisterTodoPage from './RegisterTodoPage';
import RegisterTodoDetail from './RegisterTodoDetail';
import TodoStatusPage from './TodoStatusPage';
import InvalidAccess from './components/InvalidAccess';

// Types
type StageType = 'todo' | 'state' | 'regist';

interface TodoDescription {
  desc1: string;
  desc2: string;
}

// Constants
const TODO_DESCRIPTIONS: Record<string, TodoDescription> = {
  [ToDoMgmtSubType.JEBS_AI_RECITATION]: {
    desc1:
      '국내최초! AI로 평가되는 암송인증제! 응시권을 사용하여 학생에게 인증제 할 일을 등록해주세요.',
    desc2: '학생은 본인 계정으로 접속하여 인증제를 이용할 수 있습니다. ',
  },
  [ToDoMgmtSubType.JEBSON]: {
    desc1: '온라인 복습 과제, 젭스온!',
    desc2:
      '학생에게 젭스온 할 일을 등록해 주세요. 학생은 본인 계정으로 접속하여 젭스온을 이용할 수 있습니다.',
  },
  [ToDoMgmtSubType.JEBSPL]: {
    desc1: '주일학교용 온라인 과제, 젭스플!',
    desc2:
      '이용권을 사용하여 학생에게 젭스플 할 일을 등록해주세요. 학생은 본인 계정으로 접속하여 젭스플을 이용할 수 있습니다.',
  },
} as const;

// Utility functions
const isRecitationType = (type: string | null): boolean =>
  type === ToDoMgmtSubType.RECITATION;

const getDefaultStage = (subType: string, stage?: string): StageType =>
  (stage as StageType) || (isRecitationType(subType) ? 'state' : 'todo');

/**
 * 할일 관리 컴포넌트
 * 구인증제의 경우 할일현황 탭(stage=state)만 접근 가능하며, 다른 탭 접근 시 리다이렉트 처리
 */
const TodoMgmt = () => {
  const navigate = useNavigate();
  const { classId, subType: rawSubType } = useParams();
  const subType = rawSubType?.toUpperCase() || '';
  const [params, setParams] = useSearchParams();
  const [stageTab, setStageTab] = useState<StageType>('todo');

  const setSearchParams = (
    key: string,
    value: string,
    additionalParams?: Record<string, string>,
  ) => {
    const newParams = new URLSearchParams(params);
    // stage를 제외한 모든 파라미터 제거
    newParams.forEach((_, paramKey) => {
      if (paramKey !== 'stage') {
        newParams.delete(paramKey);
      }
    });
    // 기본 파라미터 설정
    newParams.set(key, value);
    // 추가 파라미터가 있다면 설정
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([paramKey, paramValue]) => {
        newParams.set(paramKey, paramValue);
      });
    }
    setParams(newParams);
    return newParams;
  };

  useEffect(() => {
    const type = params.get('type');
    const stage = params.get('stage');
    const { state } = window.history.state || {};
    const isIntentionalNavigation = state?.usr?.intentionalRecitationNavigation;

    if (isIntentionalNavigation) return;

    // 구인증제 관련 페이지/파라미터인 경우
    if (isRecitationType(subType) || isRecitationType(type)) {
      // 현재 URL이 /recitation이 아닌 경우 리다이렉트
      if (subType !== 'RECITATION') {
        navigate(
          `/lms/${classId}/todo-management/recitation?stage=state&type=${ToDoMgmtSubType.RECITATION}`,
          { replace: true },
        );
        return;
      }

      // 이미 recitation 페이지인 경우 stage만 확인
      if (stage !== 'state') {
        const newParams = setSearchParams('stage', 'state', {
          type: ToDoMgmtSubType.RECITATION,
        });
        navigate(`${window.location.pathname}?${newParams.toString()}`, {
          replace: true,
        });
        return;
      }
    }

    // 구인증제가 아닌 경우에만 stage 설정
    setStageTab(getDefaultStage(subType, stage));
  }, [params, subType, navigate, classId]);

  const description = TODO_DESCRIPTIONS[
    subType as keyof typeof TODO_DESCRIPTIONS
  ] || {
    desc1: '',
    desc2: '',
  };

  const renderHeader = () => (
    <header className="px-5 xl:px-0">
      <TextWinds type="title_h3" className="mb-[2px]">
        {subType === ToDoMgmtSubType.RECITATION ? (
          <span>(구) 인증제 할일 현황 조회</span>
        ) : (
          <span>{getSubTypeName(subType)} 관리</span>
        )}
      </TextWinds>
      <TextWinds type="content_body3">
        {description.desc1}
        <br />
        {description.desc2}
      </TextWinds>
      {subType === ToDoMgmtSubType.JEBS_AI_RECITATION && (
        <div className="mt-3 flex w-full flex-col gap-2 xl:max-w-[500px] xl:flex-row">
          <HFbutton
            height={36}
            onClick={() =>
              openNewWindow(
                'https://jebs-notice.oopy.io/16f768f7-7a39-8042-884c-f4c58f453684',
              )
            }
          >
            AI인증제 안내
          </HFbutton>
          <HFbutton
            height={36}
            onClick={() => {
              navigate(
                `/lms/${classId}/todo-management/recitation?stage=state&type=${ToDoMgmtSubType.RECITATION}`,
                {
                  replace: true,
                  state: { intentionalRecitationNavigation: true },
                },
              );
            }}
          >
            (구) 인증제 할일 현황 조회
          </HFbutton>
        </div>
      )}
    </header>
  );

  const renderContent = () => {
    // 구인증제이면서 할일현황 탭(stage=state)이 아닌 경우 잘못된 접근 UI 표시
    if (isRecitationType(subType) && stageTab !== 'state') {
      return <InvalidAccess classId={classId} />;
    }

    if (stageTab === 'regist') {
      return isRecitationType(subType) ? (
        <InvalidAccess classId={classId} />
      ) : (
        <RegisterTodoDetail />
      );
    }

    return (
      <section className="my-5 bg-white py-4 xl:rounded-lg xl:p-4 xl:pt-6 xl:shadow-card">
        <div className="mx-5 flex gap-x-4 border-b border-gray4 pb-[18px] xl:mx-0">
          {!isRecitationType(subType) && (
            <Buttons
              type="text"
              color={stageTab === 'todo' ? 'purple5' : 'gray7'}
              onClick={() => setSearchParams('stage', 'todo')}
              className="gap-x-1"
            >
              <PencilIcon className="h-6 w-6" />
              <TextWinds
                type="title_h2"
                color={stageTab === 'todo' ? 'purple5' : 'gray7'}
              >
                할 일 등록
              </TextWinds>
            </Buttons>
          )}
          <Buttons
            type="text"
            color={stageTab === 'state' ? 'purple5' : 'gray7'}
            onClick={() => setSearchParams('stage', 'state')}
            className="gap-x-1"
          >
            <img
              src={
                process.env.PUBLIC_URL + stageTab === 'state'
                  ? '/svg/icon-chart-on.svg'
                  : '/svg/icon-chart.svg'
              }
              alt=""
            />
            <TextWinds
              type="title_h2"
              color={stageTab === 'state' ? 'purple5' : 'gray7'}
            >
              할 일 현황
            </TextWinds>
          </Buttons>
        </div>
        {stageTab === 'todo' ? <RegisterTodoPage /> : <TodoStatusPage />}
      </section>
    );
  };

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      {renderHeader()}
      {renderContent()}
    </div>
  );
};

export default TodoMgmt;
